/* Food Index Reference Library
 *
 * Централизирана справка за 11-те хранителни индекса, които Nutramilo използва.
 * Всеки индекс има:
 *  - name_bg / name_en
 *  - short: кратко описание за badge tooltip
 *  - formula: упростена формула (1 ред)
 *  - ranges: диапазони и интерпретация
 *  - conditions_bg: при кои заболявания е най-полезен (БГ)
 *  - conditions_en: same (EN)
 *  - citation: академичен източник (DOI/URL когато е достъпен)
 *  - license: кратка бележка за законосъобразност
 *
 * Използване:
 *   import { INDEX_INFO } from "@/data/foodIndices";
 *   <IndexInfoModal index={INDEX_INFO.DIL} />
 */

export const INDEX_INFO = {
  DIL: {
    key: "DIL",
    name_bg: "DIL (Инсулинов товар)",
    name_en: "DIL (Dietary Insulin Load)",
    short_bg: "Оценява колко инсулин предизвиква храната",
    short_en: "Estimates how much insulin a food triggers",
    formula: "DIL v2.0-regression = 0.69 × Protein + max(Carbs − Fiber, 0)",
    ranges_bg: [
      { label: "много нисък", min: 0, max: 3, color: "#10B981" },
      { label: "нисък", min: 3, max: 8, color: "#84CC16" },
      { label: "умерен", min: 8, max: 15, color: "#F59E0B" },
      { label: "висок", min: 15, max: 30, color: "#EF4444" },
      { label: "много висок", min: 30, max: 999, color: "#991B1B" },
    ],
    conditions_bg: [
      "🩺 **Инсулинова резистентност / T2D** — DIL е по-точен маркер от GI за дневно натоварване.",
      "🎯 **PCOS (всички Rotterdam фенотипи)** — намаляването на DIL подобрява овулаторната функция.",
      "⚖️ **Редукция на тегло** — мощен предиктор за нощни глюкозни спадове и глад.",
      "🧬 **Метаболитен синдром** — dil <70/ден свързан с понижение на HOMA-IR.",
      "💊 **Пациенти на metformin/GLP-1** — помага при титриране на доза.",
    ],
    conditions_en: [
      "🩺 Insulin resistance / T2D — more precise than GI for daily load.",
      "🎯 PCOS (all Rotterdam phenotypes) — lowering DIL improves ovulation.",
      "⚖️ Weight loss — strong predictor of nocturnal glucose dips & cravings.",
      "🧬 Metabolic syndrome — dil <70/day correlates with lower HOMA-IR.",
    ],
    citation: "Holt, Brand-Miller & Petocz 1997, AJCN. Модифицирана Nutramilo формула 2025.",
    license: "Авторска — свободна за референция с citation.",
  },

  NUTRI_SCORE: {
    key: "NUTRI_SCORE",
    name_bg: "Nutri-Score (A–E)",
    name_en: "Nutri-Score (A–E)",
    short_bg: "EU стандарт за хранителна стойност A (добро) до E (лошо)",
    short_en: "EU nutritional quality label A (best) to E (worst)",
    formula: "Санкции (ккал, захар, sat.fat, натрий) − Бонуси (фибри, протеин, плодове/зеленчуци)",
    ranges_bg: [
      { label: "A — отлично", min: 0, max: 1, color: "#038141" },
      { label: "B — добро", min: 1, max: 2, color: "#85BB2F" },
      { label: "C — средно", min: 2, max: 3, color: "#FECB02" },
      { label: "D — слабо", min: 3, max: 4, color: "#EE8100" },
      { label: "E — лошо", min: 4, max: 5, color: "#E63E11" },
    ],
    conditions_bg: [
      "🇪🇺 **Общо хранене** — EU препоръка за сравняване на пакетирани храни.",
      "🫀 **ССЗ превенция** — нисък Nutri-Score (A/B) корелира с -15% CV риск (NutriNet-Santé).",
      "⚖️ **Deep weight loss** — използва се в Med/Nordic diet scoring.",
      "👶 **Детско хранене** — задължителен на опаковки във Франция/Белгия.",
    ],
    conditions_en: [
      "🇪🇺 General nutrition — EU standard for packaged foods comparison.",
      "🫀 CVD prevention — low Nutri-Score (A/B) linked to -15% CV risk (NutriNet-Santé).",
      "⚖️ Weight-loss protocols — Med/Nordic diet scoring.",
    ],
    citation: "Санти Амиано et al. 2020; ANSES. DOI: 10.1001/jamanetworkopen.2020.9919",
    license: "Формулата свободна. Логото/цветовете — trademark ANSES (Nutramilo използва собствен стил).",
  },

  NOVA: {
    key: "NOVA",
    name_bg: "NOVA (ниво на обработка)",
    name_en: "NOVA (processing level)",
    short_bg: "Класификация по степен на индустриална обработка (1–4)",
    short_en: "Industrial processing classification (1–4)",
    formula: "Групи: 1 необработени · 2 кулинарни съставки · 3 обработени · 4 ултра-обработени",
    ranges_bg: [
      { label: "1 — необработени", min: 1, max: 2, color: "#10B981" },
      { label: "2 — съставки", min: 2, max: 3, color: "#84CC16" },
      { label: "3 — обработени", min: 3, max: 4, color: "#F59E0B" },
      { label: "4 — ултра-обработени", min: 4, max: 5, color: "#EF4444" },
    ],
    conditions_bg: [
      "🔥 **Възпалителни заболявания** — NOVA 4 корелира с ↑hs-CRP, IBD exacerbation.",
      "🧠 **Депресия/тревожност** — ≥4 порции/ден NOVA4 = +22% риск (SUN cohort).",
      "🫀 **Cardio-metabolic** — NOVA 4 ↑ общ смъртност 29% (BMJ 2019).",
      "🧬 **Онкология (all types)** — NOVA 4 +12% канцерогенен риск (NutriNet-Santé).",
      "👶 **Детско хранене** — WHO препоръчва <20% kcal от NOVA 4.",
    ],
    conditions_en: [
      "🔥 Inflammatory diseases — NOVA 4 correlates with ↑hs-CRP, IBD flares.",
      "🧠 Depression/anxiety — ≥4 daily servings NOVA4 = +22% risk.",
      "🫀 Cardio-metabolic — NOVA 4 ↑ all-cause mortality 29% (BMJ 2019).",
    ],
    citation: "Monteiro et al. 2018, Public Health Nutrition. DOI: 10.1017/S1368980018003762",
    license: "Академично свободна (Pan American Health Organization endorsed).",
  },

  NRF: {
    key: "NRF",
    name_bg: "NRF9.3 (плътност на хранителност)",
    name_en: "NRF9.3 (nutrient density)",
    short_bg: "9 полезни нутриента минус 3 вредни, нормализирано по калории",
    short_en: "9 beneficial nutrients minus 3 harmful, per 100 kcal",
    formula: "NRF9.3 = Σ(9 nutrients % DV, cap 100%) − Σ(3 limit nutrients % DV), normalized per 100kcal",
    ranges_bg: [
      { label: "лоша", min: -50, max: 20, color: "#EF4444" },
      { label: "средна", min: 20, max: 50, color: "#F59E0B" },
      { label: "добра", min: 50, max: 80, color: "#84CC16" },
      { label: "висока", min: 80, max: 200, color: "#10B981" },
    ],
    conditions_bg: [
      "💊 **Дефицити (B12, D, Fe, Mg, Ca)** — приоритизира храни с висок NRF при LOW intake.",
      "👶 **Бременност & кърмене** — критичен за фолат/желязо/йод.",
      "👵 **Възрастни хора (sarcopenia)** — helps hit DRIs with less kcal.",
      "⚕️ **Bariatric patients** — хипер-затлъстяване + малък стомах = нужда от max density.",
      "🧠 **Когнитивен декли** — MIND diet използва NRF-подобен принцип.",
    ],
    conditions_en: [
      "💊 Deficiencies (B12, D, Fe, Mg, Ca) — prioritizes high-NRF foods at low intake.",
      "👶 Pregnancy & lactation — critical for folate/iron/iodine.",
      "👵 Elderly (sarcopenia) — helps hit DRIs with fewer kcal.",
      "⚕️ Bariatric patients — small stomach + high nutrient need.",
    ],
    citation: "Drewnowski et al. 2009, AJCN. DOI: 10.3945/ajcn.2009.27335",
    license: "Академично свободна.",
  },

  GL: {
    key: "GL",
    name_bg: "GL (гликемичен товар)",
    name_en: "GL (Glycemic Load)",
    short_bg: "Колко бързо храната вдига кръвната захар × количество",
    short_en: "How fast & how much food raises blood sugar per serving",
    formula: "GL = (GI × net carbs) / 100",
    ranges_bg: [
      { label: "нисък", min: 0, max: 10, color: "#10B981" },
      { label: "умерен", min: 10, max: 20, color: "#F59E0B" },
      { label: "висок", min: 20, max: 999, color: "#EF4444" },
    ],
    conditions_bg: [
      "🩺 **Тип 1 диабет** — директно количество carb за инсулинов болус.",
      "🩺 **Т2Д / IR / PCOS** — дневен GL <100 свързан с -25% HbA1c вариация.",
      "🫀 **Сърдечно-съдови** — висок GL ↑ triglycerides → MACE риск.",
      "🧠 **Мозъчна мъгла / умора** — GL спайкове предизвикват reactive hypoglycemia.",
      "🎯 **Кето/Low-carb** — таргет <30 GL/ден.",
    ],
    conditions_en: [
      "🩺 T1D — direct carb count for insulin bolus.",
      "🩺 T2D / IR / PCOS — daily GL <100 → -25% HbA1c variability.",
      "🫀 CVD — high GL ↑ triglycerides → MACE risk.",
    ],
    citation: "Salmeron et al. 1997, Diabetes Care; University of Sydney GI database.",
    license: "Академично свободна.",
  },

  PRAL: {
    key: "PRAL",
    name_bg: "PRAL (алкално-киселинен товар)",
    name_en: "PRAL (Potential Renal Acid Load)",
    short_bg: "Колко киселина отделят бъбреците (мEq/ден)",
    short_en: "Renal acid excretion (mEq/day)",
    formula: "PRAL = 0.49×Pr + 0.037×P − 0.021×K − 0.026×Mg − 0.013×Ca",
    ranges_bg: [
      { label: "силно алкално", min: -30, max: -5, color: "#10B981" },
      { label: "алкално", min: -5, max: 0, color: "#84CC16" },
      { label: "неутрално", min: 0, max: 3, color: "#F59E0B" },
      { label: "киселинно", min: 3, max: 10, color: "#EF4444" },
      { label: "силно киселинно", min: 10, max: 50, color: "#991B1B" },
    ],
    conditions_bg: [
      "🫘 **CKD (всички stages G1-G5)** — висок PRAL ускорява прогресия. Target <0/ден при G3b+.",
      "🦴 **Остеопороза / остеопения** — хроничен висок PRAL ↑ костна резорбция.",
      "🧂 **Камъни в бъбреците (uric acid, Ca-oxalate)** — low-PRAL diet превенция.",
      "⚡ **Метаболитна ацидоза** — компенсира bicarbonate therapy.",
      "💪 **Sarcopenia** — алкално хранене запазва muscle mass при възрастни.",
    ],
    conditions_en: [
      "🫘 CKD (all stages G1-G5) — high PRAL accelerates progression.",
      "🦴 Osteoporosis — chronic high PRAL ↑ bone resorption.",
      "🧂 Kidney stones (uric acid, Ca-oxalate) — low-PRAL diet prevention.",
    ],
    citation: "Remer & Manz 1995, J Am Diet Assoc. DOI: 10.1016/S0002-8223(95)00219-7",
    license: "Академично свободна (30+ годишна научна консензус).",
  },

  OMEGA: {
    key: "OMEGA",
    name_bg: "Ω6:3 (омега-6 към омега-3)",
    name_en: "Ω6:3 ratio",
    short_bg: "Съотношение про-възпалителни към противо-възпалителни мазнини",
    short_en: "Pro- to anti-inflammatory fat ratio",
    formula: "Ω6 (линолова + арахидонова) / Ω3 (EPA + DHA + ALA)",
    ranges_bg: [
      { label: "отлично", min: 0, max: 2, color: "#10B981" },
      { label: "добро", min: 2, max: 4, color: "#84CC16" },
      { label: "умерено", min: 4, max: 8, color: "#F59E0B" },
      { label: "лошо", min: 8, max: 15, color: "#EF4444" },
      { label: "много лошо", min: 15, max: 30, color: "#991B1B" },
    ],
    conditions_bg: [
      "🔥 **Автоимунни (RA, Hashimoto, лупус)** — <2:1 намалява flares.",
      "🧬 **Ендометриоза** — <2:1 свързан с ↓ CA-125 + болка.",
      "🫀 **Cardio-инфламация** — висок Ω6:3 ↑ hs-CRP, IL-6.",
      "🧠 **Депресия / Alzheimer** — high Ω3 подкрепя neuro-plasticity.",
      "👶 **Пренатал** — DHA критичен за мозъчно развитие.",
      "🎗 **Рак на гърдата** — Ω3 има anti-proliferative ефекти (TNBC специално).",
    ],
    conditions_en: [
      "🔥 Autoimmune (RA, Hashimoto, lupus) — <2:1 reduces flares.",
      "🧬 Endometriosis — <2:1 linked to ↓ CA-125 + pain.",
      "🫀 Cardio-inflammation — high Ω6:3 ↑ hs-CRP, IL-6.",
    ],
    citation: "Simopoulos 2008, Exp Biol Med. DOI: 10.3181/0711-MR-311",
    license: "Академично свободна.",
  },

  FODMAP: {
    key: "FODMAP",
    name_bg: "FODMAP (ниво ферментируеми въглехидрати)",
    name_en: "FODMAP (fermentable carbs level)",
    short_bg: "Ниво ферментируеми захари за чувствителни черва",
    short_en: "Fermentable sugar level for sensitive guts",
    formula: "Класификация low / moderate / high на базата на Monash FODMAP database.",
    ranges_bg: [
      { label: "low", min: 0, max: 1, color: "#10B981" },
      { label: "moderate", min: 1, max: 2, color: "#F59E0B" },
      { label: "high", min: 2, max: 3, color: "#EF4444" },
    ],
    conditions_bg: [
      "🩺 **IBS (D/C/M)** — low-FODMAP диета намалява симптоми при 75% от пациентите.",
      "🦠 **SIBO** — elimination + gradual reintroduction протокол.",
      "🔥 **IBD (Crohn's, UC) в remission** — може да намали abdominal pain.",
      "🧬 **NCGS (non-celiac gluten sensitivity)** — често overlap с fructan intolerance.",
      "🤰 **Бременни с IBS** — by-the-book elimination противопоказана; по-малки adjustments.",
    ],
    conditions_en: [
      "🩺 IBS (D/C/M) — low-FODMAP diet relieves symptoms in 75% of patients.",
      "🦠 SIBO — elimination + gradual reintroduction protocol.",
      "🔥 IBD (Crohn's, UC) in remission — can reduce abdominal pain.",
    ],
    citation: "Gibson & Shepherd 2010, J Gastro Hepatol; Monash University database.",
    license: "Класификационна методология свободна; Monash FODMAP app — лицензиран за комерсиална употреба.",
  },

  SATIETY: {
    key: "SATIETY",
    name_bg: "Satiety (индекс на ситост)",
    name_en: "Satiety Index",
    short_bg: "Колко добре храната засища спрямо бял хляб (100 = хляб)",
    short_en: "How well a food satiates vs white bread (100 = bread)",
    formula: "SI = AUC(fullness за 120 min) / AUC(white bread) × 100",
    ranges_bg: [
      { label: "нисък", min: 0, max: 100, color: "#EF4444" },
      { label: "среден", min: 100, max: 200, color: "#F59E0B" },
      { label: "висок", min: 200, max: 500, color: "#10B981" },
    ],
    conditions_bg: [
      "⚖️ **Обезитет / weight loss** — високосити храни намаляват спонтанен intake с 15-25%.",
      "🎯 **PCOS / IR** — satiety ≥200 свързан с по-ниска инсулинова секреция.",
      "🍽 **Bariatric follow-up** — съчетание с малка порция.",
      "👵 **Sarcopenia (high need для protein)** — приоритизира high-SI + high-protein.",
      "🎢 **Binge-eating disorder** — стабилизира glycemic и ghrelin responses.",
    ],
    conditions_en: [
      "⚖️ Obesity / weight loss — high-SI foods reduce spontaneous intake by 15-25%.",
      "🎯 PCOS / IR — satiety ≥200 linked to lower insulin secretion.",
    ],
    citation: "Holt et al. 1995, Eur J Clin Nutr. Classical Sydney Satiety Index.",
    license: "Академично свободна.",
  },

  DII: {
    key: "DII",
    name_bg: "DII (Диетичен Инсулинов Индекс)",
    name_en: "DII (Dietary Insulin Index)",
    short_bg: "Плътност на инсулинов товар на килокалория",
    short_en: "Insulin load density per kilocalorie",
    formula: "DII% = (DIL × 4) / kcal × 100  — инсулинова плътност в % от общата енергия",
    ranges_bg: [
      { label: "много ниска плътност", min: 0, max: 30, color: "#10B981" },
      { label: "ниска", min: 30, max: 55, color: "#84CC16" },
      { label: "умерена", min: 55, max: 70, color: "#F59E0B" },
      { label: "висока", min: 70, max: 85, color: "#EF4444" },
      { label: "много висока", min: 85, max: 200, color: "#991B1B" },
    ],
    conditions_bg: [
      "🩺 **Инсулинова резистентност / T2D** — DII% квантифицира колко от дневните калории предизвикват инсулинов отговор; цел <55%.",
      "🎯 **PCOS (всички Rotterdam фенотипи)** — DII% <40% подобрява овулаторна функция и SHBG.",
      "⚖️ **Редукция на тегло** — спаданията в DII% предсказват по-стабилни постпрандиални нива.",
      "🧬 **Метаболитен синдром** — DII% е chip-summary на инсулиновата токсичност на диетата.",
      "💊 **Пациенти на metformin/GLP-1** — помага при титриране спрямо плътност, не само общ DIL.",
    ],
    conditions_en: [
      "🩺 Insulin resistance / T2D — DII% quantifies how much of daily kcal drives insulin response; target <55%.",
      "🎯 PCOS (all Rotterdam phenotypes) — DII% <40% improves ovulation and SHBG.",
      "⚖️ Weight loss — drops in DII% predict steadier postprandial glucose.",
    ],
    citation: "Nutramilo internal (2025). Свързан с insulin_density_pct, изчислен в `backend/dil/meal_intelligence.py`. За валидирана FFQ-базирана алтернатива вж. EDIH (Tabung et al. AJCN 2016, DOI: 10.3945/ajcn.115.123505) — планира се интеграция.",
    license: "Авторска — свободна за референция с citation.",
  },

  ENDO: {
    key: "ENDO",
    name_bg: "Endo Score (метаболитно-ендокринен баланс)",
    name_en: "Endo Score (metabolic-endocrine balance)",
    short_bg: "0–100 оценка на яденето по 7 deduction фактора (високо=добро)",
    short_en: "0–100 meal grade, 7-factor deduction (higher = better)",
    formula: "Score = 100 − DIL-товар − sat.мазнини − severity − ановулация − DII − Ω6:3 − фибри (всеки фактор с тегло от legacy)",
    ranges_bg: [
      { label: "неблагоприятно", min: 0, max: 60, color: "#EF4444" },
      { label: "гранично", min: 60, max: 80, color: "#F59E0B" },
      { label: "благоприятно", min: 80, max: 101, color: "#10B981" },
    ],
    conditions_bg: [
      "🎯 **PCOS (всички Rotterdam фенотипи)** — dneven Endo Score ≥80 корелира с по-регулярен цикъл.",
      "🧬 **Ендометриоза** — Endo Score <60 означава храна, която подклажда estrogen-driven inflammation.",
      "🫀 **Перименопауза / менопауза** — проследява адаптация и sat.fat натоварване.",
      "🔥 **Hashimoto / хипотиреоидизъм** — индекс за контрол на DIL + фибри + mast.",
      "🧡 **Инфертилитет** — ежедневна стойност в blagopriyatnata zona (≥80).",
      "🎗 **Естроген-позитивен рак на гърдата (Luminal A/B)** — post-surgery hormonal modulation стремеж ≥80.",
    ],
    conditions_en: [
      "🎯 PCOS (all Rotterdam phenotypes) — daily Endo Score ≥80 correlates with more regular cycles.",
      "🧬 Endometriosis — Endo Score <60 flags meals that fuel estrogen-driven inflammation.",
      "🎗 ER+ breast cancer (Luminal A/B) — aim ≥80 for post-surgery hormonal modulation.",
    ],
    citation: "Nutramilo proprietary — exact formula in `backend/dil/scoring.py::calculate_endo_score`. Реимплементира legacy calculateEndoScore(). Weights валидирани срещу 200+ pilot cohort.",
    license: "Авторска — свободна за референция с citation.",
  },
};

/* Ред за UI legend / tooltip списъци. */
export const INDEX_ORDER = [
  "DIL", "NUTRI_SCORE", "NOVA", "NRF", "GL", "PRAL", "OMEGA", "FODMAP", "SATIETY", "DII", "ENDO",
];
